/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { defineMessages } from 'react-intl';

import { HIERARCHY_ITEM_STATUS } from '../constants/constants';

import { formatErrorResponse } from 'responseFormatterFunctions';
import { I18NError } from 'Utils';
import { fetchGet, fetchPut, fetchDelete } from 'FetchAPIs';

const baseHierarchiesURL = '/api/hierarchies';

const messages = defineMessages({
  errorLoadingArchivedHierarchyItems: 'Error loading archived hierarchy items. {message}',
  errorArchivingHierarchyItem: 'Error archiving hierarchy item. {message}',
  errorRestoringHierarchyItem: 'Error restoring hierarchy item. {message}',
  errorDeletingHierarchyItem: 'Error deleting hierarchy item. {message}',
});

export const getArchivedHierarchyItems = async (
  limit = 1000 // Hardcoding the pagination limit here because this is an accepted solution that had been used in other places
): Promise<any> =>
  fetchGet(`${baseHierarchiesURL}/workspaces/resources`, null, {
    status: HIERARCHY_ITEM_STATUS.ARCHIVED,
    limit,
  })
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingArchivedHierarchyItems, error);
    });

export const archiveHierarchyItem = async (
  hierarchyPath: string,
  userEmailId: string
): Promise<any> =>
  fetchPut(`${baseHierarchiesURL}${hierarchyPath}/archive`, null, { userEmailId }, undefined).catch(
    error => {
      throw new I18NError(messages.errorArchivingHierarchyItem, {
        message: formatErrorResponse(error),
      });
    }
  );

/** restore entity type, FYI it must be archived first */
export const restoreHierarchyItem = async (
  hierarchyPath: string,
  userEmailId: string
): Promise<any> =>
  fetchPut(`${baseHierarchiesURL}${hierarchyPath}/restore`, null, { userEmailId }, undefined).catch(
    error => {
      throw new I18NError(messages.errorRestoringHierarchyItem, {
        message: formatErrorResponse(error),
      });
    }
  );

/** delete entity type, FYI it must be archived first */
export const deleteHierarchyItem = async (
  hierarchyPath: string,
  userEmailId: string
): Promise<any> =>
  fetchDelete(`${baseHierarchiesURL}${hierarchyPath}`, null, { userEmailId }, undefined).catch(
    error => {
      throw new I18NError(messages.errorDeletingHierarchyItem, {
        message: formatErrorResponse(error),
      });
    }
  );
